import { queryOptions, useQuery } from '@tanstack/react-query';

import { ClusterAnomaly } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_ANOMALIES_QUERY_KEY = 'cluster-anomalies';

type UseClusterAnomaliesQueryParams = {
  clusterId: string;
  startTime: string;
  endTime: string;
  enabled?: boolean;
};

type UseClusterAnomaliesQuery = {
  anomalies?: ClusterAnomaly[];
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
};

const getClusterAnomaliesQueryKey = ({
  clusterId,
  startTime,
  endTime,
}: Pick<
  UseClusterAnomaliesQueryParams,
  'clusterId' | 'startTime' | 'endTime'
>) => [CLUSTER_ANOMALIES_QUERY_KEY, clusterId, startTime, endTime] as const;

const getClusterAnomaliesQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  enabled = true,
}: UseClusterAnomaliesQueryParams) =>
  queryOptions({
    queryKey: getClusterAnomaliesQueryKey({ clusterId, startTime, endTime }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getClusterAnomalies({
        clusterId,
        startTime,
        endTime,
      });
      return data.anomalies ?? [];
    },
    enabled,
  });

export const useClusterAnomaliesQuery = (
  params: UseClusterAnomaliesQueryParams
): UseClusterAnomaliesQuery => {
  const {
    isLoading,
    data: anomalies,
    error,
    refetch,
  } = useQuery(getClusterAnomaliesQueryOptions(params));

  return {
    anomalies,
    isLoading,
    error,
    refetch,
  };
};
